import type { ImportJobResponseDto } from '@trellis/api'
import { DataTable } from '@trellis/roots/data'
import { StatusBadge } from '@trellis/roots/status'
import { Typography } from '@trellis/roots/typography'

import { IMPORT_STATUS_LABELS, IMPORT_STATUS_STYLES } from '@/lib/import-status'

import { ImportValidationErrors } from '../ImportValidationErrors'
import type { ImportPreviewConfig } from '../types'

interface ImportJobOverviewProps<TRow> extends ImportPreviewConfig<TRow> {
  job: ImportJobResponseDto
}

export function ImportJobOverview<TRow>({
  job,
  previewColumns,
  getPreviewRows,
}: ImportJobOverviewProps<TRow>) {
  const previewRows = getPreviewRows(job.preview) ?? []

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-4 text-sm">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Status</dt>
            <dd className="mt-0.5">
              <StatusBadge
                status={job.status}
                styles={IMPORT_STATUS_STYLES}
                labels={IMPORT_STATUS_LABELS}
              />
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Total rows</dt>
            <dd className="font-medium">{job.totalRows}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Valid</dt>
            <dd className="font-medium text-green-700">{job.validRows}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Errors</dt>
            <dd className="font-medium text-red-600">{job.errorRows}</dd>
          </div>
        </dl>
      </div>

      {previewRows.length > 0 && (
        <div className="space-y-2">
          <Typography variant="h3" as="h2">
            Preview
          </Typography>
          <DataTable columns={previewColumns} data={previewRows} isLoading={false} />
        </div>
      )}

      {job.errors.length > 0 && (
        <div className="space-y-2">
          <Typography variant="h3" as="h2">
            Validation Errors
          </Typography>
          <ImportValidationErrors errors={job.errors} />
        </div>
      )}
    </div>
  )
}
